import React from 'react';
import { motion } from 'framer-motion';

interface TaskFlowLogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  showText?: boolean;
  animated?: boolean;
  variant?: 'default' | 'minimal' | 'colorful' | 'eye' | 'river' | 'brain' | 'rocket' | 'compass' | 'hourglass' | 'galaxy' | 'dna' | 'flower' | 'robot';
}

const TaskFlowLogo: React.FC<TaskFlowLogoProps> = ({
  size = 'md',
  showText = true,
  animated = true,
  variant = 'robot'
}) => {
  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-12 h-12',
    lg: 'w-14 h-14',
    xl: 'w-16 h-16',
    xxl: 'w-20 h-20'
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-xl',
    xl: 'text-2xl',
    xxl: 'text-3xl'
  };

  const iconSizes = {
    sm: { width: 'w-5/6 h-5/6', viewBox: '0 0 40 40' },
    md: { width: 'w-5/6 h-5/6', viewBox: '0 0 48 48' },
    lg: { width: 'w-5/6 h-5/6', viewBox: '0 0 56 56' },
    xl: { width: 'w-5/6 h-5/6', viewBox: '0 0 64 64' },
    xxl: { width: 'w-5/6 h-5/6', viewBox: '0 0 80 80' }
  };

  const getGradientColors = () => {
    switch (variant) {
      case 'colorful':
        return 'from-pink-300 via-purple-300 to-indigo-300';
      case 'minimal':
        return 'from-gray-400 to-gray-600';
      case 'eye':
        return 'from-indigo-400 via-purple-400 to-pink-400';
      case 'river':
        return 'from-blue-400 via-cyan-400 to-teal-400';
      case 'brain':
        return 'from-purple-400 via-pink-400 to-indigo-400';
      case 'rocket':
        return 'from-orange-400 via-red-400 to-pink-400';
      case 'compass':
        return 'from-emerald-400 via-teal-400 to-cyan-400';
      case 'hourglass':
        return 'from-amber-400 via-orange-400 to-red-400';
      case 'galaxy':
        return 'from-indigo-500 via-purple-500 to-pink-500';
      case 'flower':
        return 'from-rose-400 via-pink-400 to-purple-400';
      case 'robot':
        return 'from-blue-500 via-cyan-500 to-indigo-500';
      default:
        return 'from-blue-500 via-cyan-500 to-indigo-500';
    }
  };

  const getTextGradient = () => {
    switch (variant) {
      case 'colorful':
        return 'from-pink-500 via-purple-500 to-indigo-500';
      case 'minimal':
        return 'from-gray-600 to-gray-800';
      case 'eye':
        return 'from-indigo-500 via-purple-500 to-pink-500';
      case 'river':
        return 'from-blue-500 via-cyan-500 to-teal-500';
      case 'brain':
        return 'from-purple-500 via-pink-500 to-indigo-500';
      case 'rocket':
        return 'from-orange-500 via-red-500 to-pink-500';
      case 'compass':
        return 'from-emerald-500 via-teal-500 to-cyan-500';
      case 'hourglass':
        return 'from-amber-500 via-orange-500 to-red-500';
      case 'galaxy':
        return 'from-indigo-600 via-purple-600 to-pink-600';
      case 'dna':
        return 'from-green-500 via-emerald-500 to-teal-500';
      case 'flower':
        return 'from-rose-500 via-pink-500 to-purple-500';
      case 'robot':
        return 'from-blue-600 via-cyan-600 to-indigo-600';
    }
  };

  const HourglassFlowIcon = () => {
    const currentSize = iconSizes[size];
    const gradientColors = getGradientColors();

    return (
      <div className={`${sizeClasses[size]} bg-gradient-to-br ${gradientColors} rounded-3xl flex items-center justify-center shadow-2xl border-2 border-white/40 backdrop-blur-sm relative overflow-hidden group`}>
        {/* Animated sand background */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        <div className="absolute inset-0 bg-gradient-to-br from-amber-200/20 via-orange-200/10 to-red-200/20 opacity-80" />

        <svg
          className={currentSize.width}
          viewBox={currentSize.viewBox}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Top chamber */}
          <path
            d="M8 8 L32 8 L36 12 L36 20 Q36 24 24 24 Q12 24 12 20 L12 12 Z"
            fill="url(#chamberGradient)"
            opacity="0.9"
          />

          {/* Bottom chamber */}
          <path
            d="M8 32 L32 32 L36 28 L36 20 Q36 16 24 16 Q12 16 12 20 L12 28 Z"
            fill="url(#chamberGradient)"
            opacity="0.9"
          />

          {/* Hourglass frame */}
          <path
            d="M8 8 L32 8 L36 12 L36 28 L32 32 L8 32 L4 28 L4 12 Z"
            fill="none"
            stroke="rgba(255,255,255,0.4)"
            strokeWidth="1"
          />

          {/* Sand in top chamber */}
          <path
            d="M12 12 L28 12 L30 16 Q24 18 18 16 Z"
            fill="url(#sandTopGradient)"
            opacity="0.8"
          />

          {/* Sand in bottom chamber */}
          <path
            d="M12 28 L28 28 L30 24 Q24 22 18 24 Z"
            fill="url(#sandBottomGradient)"
            opacity="0.8"
          />

          {/* Flowing sand particles */}
          <circle cx="20" cy="20" r="0.8" fill="url(#sandParticle1)" opacity="0.9" />
          <circle cx="22" cy="21" r="0.6" fill="url(#sandParticle2)" opacity="0.9" />
          <circle cx="24" cy="19" r="0.7" fill="url(#sandParticle3)" opacity="0.9" />
          <circle cx="26" cy="22" r="0.5" fill="url(#sandParticle4)" opacity="0.9" />
          <circle cx="18" cy="23" r="0.6" fill="url(#sandParticle5)" opacity="0.9" />

          {/* Sand flow streams */}
          <path d="M20 20 Q20 22 20 24" stroke="rgba(255,255,255,0.6)" strokeWidth="0.5" opacity="0.8" />
          <path d="M22 21 Q22 23 22 25" stroke="rgba(255,255,255,0.6)" strokeWidth="0.5" opacity="0.8" />
          <path d="M24 19 Q24 21 24 23" stroke="rgba(255,255,255,0.6)" strokeWidth="0.5" opacity="0.8" />
          <path d="M26 22 Q26 24 26 26" stroke="rgba(255,255,255,0.6)" strokeWidth="0.5" opacity="0.8" />

          {/* Time indicators */}
          <rect x="18" y="10" width="1" height="2" fill="rgba(255,255,255,0.7)" opacity="0.9" />
          <rect x="21" y="10" width="1" height="3" fill="rgba(255,255,255,0.7)" opacity="0.9" />
          <rect x="24" y="10" width="1" height="2" fill="rgba(255,255,255,0.7)" opacity="0.9" />
          <rect x="27" y="10" width="1" height="3" fill="rgba(255,255,255,0.7)" opacity="0.9" />

          <rect x="18" y="30" width="1" height="3" fill="rgba(255,255,255,0.7)" opacity="0.9" />
          <rect x="21" y="30" width="1" height="2" fill="rgba(255,255,255,0.7)" opacity="0.9" />
          <rect x="24" y="30" width="1" height="3" fill="rgba(255,255,255,0.7)" opacity="0.9" />
          <rect x="27" y="30" width="1" height="2" fill="rgba(255,255,255,0.7)" opacity="0.9" />

          <defs>
            <linearGradient id="chamberGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F59E0B" />
              <stop offset="100%" stopColor="#EA580C" />
            </linearGradient>
            <linearGradient id="sandTopGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FDE68A" />
              <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>
            <linearGradient id="sandBottomGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#DC2626" />
              <stop offset="100%" stopColor="#B91C1C" />
            </linearGradient>
            <radialGradient id="sandParticle1" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#FBBF24" />
              <stop offset="100%" stopColor="#D97706" />
            </radialGradient>
            <radialGradient id="sandParticle2" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#EF4444" />
              <stop offset="100%" stopColor="#DC2626" />
            </radialGradient>
            <radialGradient id="sandParticle3" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#F59E0B" />
              <stop offset="100%" stopColor="#EA580C" />
            </radialGradient>
            <radialGradient id="sandParticle4" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#F97316" />
              <stop offset="100%" stopColor="#EA580C" />
            </radialGradient>
            <radialGradient id="sandParticle5" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#FB923C" />
              <stop offset="100%" stopColor="#F97316" />
            </radialGradient>
          </defs>
        </svg>

        {/* Sand flow animation */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-2000 ease-in-out" />

        {/* Hourglass glow effects */}
        <div className="absolute inset-0 rounded-3xl border-2 border-amber-300/40 animate-ping opacity-20" />
        <div className="absolute inset-1 rounded-3xl border border-amber-200/30 animate-pulse opacity-30" />

        {/* Sand particles floating effect */}
        <div className="absolute top-2 right-2 w-2 h-2 bg-amber-300 rounded-full animate-bounce opacity-40" />
        <div className="absolute bottom-2 left-2 w-1.5 h-1.5 bg-orange-300 rounded-full animate-bounce opacity-40" style={{animationDelay: '0.5s'}} />
      </div>
    );
  };

  const GalaxyFlowIcon = () => {
    const currentSize = iconSizes[size];
    const gradientColors = getGradientColors();

    return (
      <div className={`${sizeClasses[size]} bg-gradient-to-br ${gradientColors} rounded-3xl flex items-center justify-center shadow-2xl border-2 border-white/40 backdrop-blur-sm relative overflow-hidden group`}>
        <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-200/20 via-purple-200/10 to-pink-200/20 opacity-80" />

        <svg
          className={currentSize.width}
          viewBox={currentSize.viewBox}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Spiral galaxy arms */}
          <path
            d="M24 8 Q32 12 36 20 Q34 28 26 34 Q18 32 12 26 Q8 18 12 12 Q18 8 24 8"
            fill="none"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="2"
            opacity="0.6"
          />

          {/* Galaxy center */}
          <circle
            cx="24"
            cy="24"
            r="6"
            fill="url(#galaxyCenter)"
            opacity="0.95"
          />

          {/* Spiral arms with stars */}
          <path d="M24 8 Q28 14 30 20 Q26 24 22 26 Q18 22 16 18 Q18 12 22 10" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1" opacity="0.8" />
          <path d="M24 40 Q20 34 18 28 Q22 24 26 22 Q30 26 32 30 Q30 36 26 38" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1" opacity="0.8" />

          {/* Stars scattered throughout */}
          <circle cx="16" cy="16" r="0.8" fill="white" opacity="0.9" />
          <circle cx="32" cy="18" r="0.6" fill="white" opacity="0.8" />
          <circle cx="20" cy="32" r="0.7" fill="white" opacity="0.9" />
          <circle cx="28" cy="12" r="0.5" fill="white" opacity="0.7" />
          <circle cx="12" cy="28" r="0.6" fill="white" opacity="0.8" />
          <circle cx="36" cy="26" r="0.7" fill="white" opacity="0.9" />
          <circle cx="18" cy="8" r="0.4" fill="white" opacity="0.6" />
          <circle cx="30" cy="36" r="0.5" fill="white" opacity="0.7" />

          {/* Orbital rings */}
          <circle cx="24" cy="24" r="12" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" opacity="0.6" />
          <circle cx="24" cy="24" r="18" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="0.3" opacity="0.4" />

          {/* Flow particles in orbit */}
          <circle cx="24" cy="12" r="0.8" fill="rgba(255,255,255,0.8)" opacity="1" />
          <circle cx="30" cy="18" r="0.6" fill="rgba(255,255,255,0.8)" opacity="1" />
          <circle cx="32" cy="24" r="0.7" fill="rgba(255,255,255,0.8)" opacity="1" />
          <circle cx="30" cy="30" r="0.5" fill="rgba(255,255,255,0.8)" opacity="1" />
          <circle cx="24" cy="36" r="0.6" fill="rgba(255,255,255,0.8)" opacity="1" />
          <circle cx="18" cy="30" r="0.7" fill="rgba(255,255,255,0.8)" opacity="1" />
          <circle cx="16" cy="24" r="0.5" fill="rgba(255,255,255,0.8)" opacity="1" />
          <circle cx="18" cy="18" r="0.6" fill="rgba(255,255,255,0.8)" opacity="1" />

          <defs>
            <radialGradient id="galaxyCenter" cx="50%" cy="40%" r="70%">
              <stop offset="0%" stopColor="#FBBF24" />
              <stop offset="50%" stopColor="#F59E0B" />
              <stop offset="100%" stopColor="#D97706" />
            </radialGradient>
          </defs>
        </svg>

        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-2000 ease-in-out" />
        <div className="absolute inset-0 rounded-3xl border-2 border-indigo-300/40 animate-ping opacity-20" />
        <div className="absolute inset-1 rounded-3xl border border-indigo-200/30 animate-pulse opacity-30" />
      </div>
    );
  };

  const DNAFlowIcon = () => {
    const currentSize = iconSizes[size];
    const gradientColors = getGradientColors();

    return (
      <div className={`${sizeClasses[size]} bg-gradient-to-br ${gradientColors} rounded-3xl flex items-center justify-center shadow-2xl border-2 border-white/40 backdrop-blur-sm relative overflow-hidden group`}>
        <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        <div className="absolute inset-0 bg-gradient-to-br from-green-200/20 via-emerald-200/10 to-teal-200/20 opacity-80" />

        <svg
          className={currentSize.width}
          viewBox={currentSize.viewBox}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* DNA backbone strands */}
          <path d="M16 8 Q20 16 16 24 Q20 32 16 40" stroke="rgba(255,255,255,0.6)" strokeWidth="2" fill="none" opacity="0.8" />
          <path d="M24 8 Q20 16 24 24 Q20 32 24 40" stroke="rgba(255,255,255,0.6)" strokeWidth="2" fill="none" opacity="0.8" />

          {/* DNA base pairs */}
          <ellipse cx="20" cy="12" rx="4" ry="1.5" fill="url(#dnaPair1)" opacity="0.9" transform="rotate(45 20 12)" />
          <ellipse cx="20" cy="20" rx="4" ry="1.5" fill="url(#dnaPair2)" opacity="0.9" transform="rotate(-45 20 20)" />
          <ellipse cx="20" cy="28" rx="4" ry="1.5" fill="url(#dnaPair3)" opacity="0.9" transform="rotate(45 20 28)" />
          <ellipse cx="20" cy="36" rx="4" ry="1.5" fill="url(#dnaPair4)" opacity="0.9" transform="rotate(-45 20 36)" />

          {/* Flow particles along strands */}
          <circle cx="18" cy="14" r="0.8" fill="rgba(255,255,255,0.9)" opacity="1" />
          <circle cx="22" cy="18" r="0.6" fill="rgba(255,255,255,0.9)" opacity="1" />
          <circle cx="18" cy="22" r="0.7" fill="rgba(255,255,255,0.9)" opacity="1" />
          <circle cx="22" cy="26" r="0.5" fill="rgba(255,255,255,0.9)" opacity="1" />
          <circle cx="18" cy="30" r="0.6" fill="rgba(255,255,255,0.9)" opacity="1" />
          <circle cx="22" cy="34" r="0.7" fill="rgba(255,255,255,0.9)" opacity="1" />

          {/* Nucleotide bases */}
          <circle cx="14" cy="12" r="1" fill="url(#nucleotide1)" opacity="0.9" />
          <circle cx="26" cy="12" r="1" fill="url(#nucleotide2)" opacity="0.9" />
          <circle cx="14" cy="20" r="1" fill="url(#nucleotide3)" opacity="0.9" />
          <circle cx="26" cy="20" r="1" fill="url(#nucleotide4)" opacity="0.9" />
          <circle cx="14" cy="28" r="1" fill="url(#nucleotide5)" opacity="0.9" />
          <circle cx="26" cy="28" r="1" fill="url(#nucleotide6)" opacity="0.9" />
          <circle cx="14" cy="36" r="1" fill="url(#nucleotide7)" opacity="0.9" />
          <circle cx="26" cy="36" r="1" fill="url(#nucleotide8)" opacity="0.9" />

          <defs>
            <linearGradient id="dnaPair1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#10B981" />
              <stop offset="100%" stopColor="#059669" />
            </linearGradient>
            <linearGradient id="dnaPair2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3B82F6" />
              <stop offset="100%" stopColor="#1D4ED8" />
            </linearGradient>
            <linearGradient id="dnaPair3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#8B5CF6" />
              <stop offset="100%" stopColor="#7C3AED" />
            </linearGradient>
            <linearGradient id="dnaPair4" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F59E0B" />
              <stop offset="100%" stopColor="#D97706" />
            </linearGradient>
            <radialGradient id="nucleotide1" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#10B981" />
              <stop offset="100%" stopColor="#047857" />
            </radialGradient>
            <radialGradient id="nucleotide2" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#3B82F6" />
              <stop offset="100%" stopColor="#1E40AF" />
            </radialGradient>
            <radialGradient id="nucleotide3" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#8B5CF6" />
              <stop offset="100%" stopColor="#6D28D9" />
            </radialGradient>
            <radialGradient id="nucleotide4" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#F59E0B" />
              <stop offset="100%" stopColor="#D97706" />
            </radialGradient>
            <radialGradient id="nucleotide5" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#EF4444" />
              <stop offset="100%" stopColor="#DC2626" />
            </radialGradient>
            <radialGradient id="nucleotide6" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#EC4899" />
              <stop offset="100%" stopColor="#DB2777" />
            </radialGradient>
            <radialGradient id="nucleotide7" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#06B6D4" />
              <stop offset="100%" stopColor="#0891B2" />
            </radialGradient>
            <radialGradient id="nucleotide8" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#84CC16" />
              <stop offset="100%" stopColor="#65A30D" />
            </radialGradient>
          </defs>
        </svg>

        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-2000 ease-in-out" />
        <div className="absolute inset-0 rounded-3xl border-2 border-green-300/40 animate-ping opacity-20" />
        <div className="absolute inset-1 rounded-3xl border border-green-200/30 animate-pulse opacity-30" />
      </div>
    );
  };

  const FlowerFlowIcon = () => {
    const currentSize = iconSizes[size];
    const gradientColors = getGradientColors();

    return (
      <div className={`${sizeClasses[size]} bg-gradient-to-br ${gradientColors} rounded-3xl flex items-center justify-center shadow-2xl border-2 border-white/40 backdrop-blur-sm relative overflow-hidden group`}>
        <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        <div className="absolute inset-0 bg-gradient-to-br from-rose-200/20 via-pink-200/10 to-purple-200/20 opacity-80" />

        <svg
          className={currentSize.width}
          viewBox={currentSize.viewBox}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Flower center */}
          <circle
            cx="24"
            cy="24"
            r="4"
            fill="url(#flowerCenter)"
            opacity="0.95"
          />

          {/* Petals representing task stages */}
          <ellipse cx="24" cy="16" rx="6" ry="3" fill="url(#petal1)" opacity="0.9" transform="rotate(0 24 16)" />
          <ellipse cx="24" cy="16" rx="6" ry="3" fill="url(#petal2)" opacity="0.9" transform="rotate(45 24 16)" />
          <ellipse cx="24" cy="16" rx="6" ry="3" fill="url(#petal3)" opacity="0.9" transform="rotate(90 24 16)" />
          <ellipse cx="24" cy="16" rx="6" ry="3" fill="url(#petal4)" opacity="0.9" transform="rotate(135 24 16)" />
          <ellipse cx="24" cy="16" rx="6" ry="3" fill="url(#petal5)" opacity="0.9" transform="rotate(180 24 16)" />
          <ellipse cx="24" cy="16" rx="6" ry="3" fill="url(#petal6)" opacity="0.9" transform="rotate(225 24 16)" />
          <ellipse cx="24" cy="16" rx="6" ry="3" fill="url(#petal7)" opacity="0.9" transform="rotate(270 24 16)" />
          <ellipse cx="24" cy="16" rx="6" ry="3" fill="url(#petal8)" opacity="0.9" transform="rotate(315 24 16)" />

          {/* Flow lines to petals */}
          <path d="M24 20 L24 16" stroke="rgba(255,255,255,0.4)" strokeWidth="1" opacity="0.8" />
          <path d="M20 22 L22 18" stroke="rgba(255,255,255,0.4)" strokeWidth="1" opacity="0.8" />
          <path d="M22 26 L20 22" stroke="rgba(255,255,255,0.4)" strokeWidth="1" opacity="0.8" />
          <path d="M26 26 L26 22" stroke="rgba(255,255,255,0.4)" strokeWidth="1" opacity="0.8" />
          <path d="M28 22 L26 18" stroke="rgba(255,255,255,0.4)" strokeWidth="1" opacity="0.8" />

          {/* Growth particles */}
          <circle cx="24" cy="14" r="0.6" fill="rgba(255,255,255,0.9)" opacity="1" />
          <circle cx="21" cy="17" r="0.5" fill="rgba(255,255,255,0.9)" opacity="1" />
          <circle cx="27" cy="17" r="0.4" fill="rgba(255,255,255,0.9)" opacity="1" />
          <circle cx="20" cy="21" r="0.5" fill="rgba(255,255,255,0.9)" opacity="1" />
          <circle cx="28" cy="21" r="0.6" fill="rgba(255,255,255,0.9)" opacity="1" />

          <defs>
            <radialGradient id="flowerCenter" cx="50%" cy="40%" r="70%">
              <stop offset="0%" stopColor="#FBBF24" />
              <stop offset="100%" stopColor="#F59E0B" />
            </radialGradient>
            <linearGradient id="petal1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F87171" />
              <stop offset="100%" stopColor="#DC2626" />
            </linearGradient>
            <linearGradient id="petal2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FB7185" />
              <stop offset="100%" stopColor="#E11D48" />
            </linearGradient>
            <linearGradient id="petal3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#A78BFA" />
              <stop offset="100%" stopColor="#7C3AED" />
            </linearGradient>
            <linearGradient id="petal4" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#C084FC" />
              <stop offset="100%" stopColor="#A855F7" />
            </linearGradient>
            <linearGradient id="petal5" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FBBF24" />
              <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>
            <linearGradient id="petal6" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#34D399" />
              <stop offset="100%" stopColor="#10B981" />
            </linearGradient>
            <linearGradient id="petal7" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#6EE7B7" />
              <stop offset="100%" stopColor="#34D399" />
            </linearGradient>
            <linearGradient id="petal8" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#7DD3FC" />
              <stop offset="100%" stopColor="#0EA5E9" />
            </linearGradient>
          </defs>
        </svg>

        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-2000 ease-in-out" />
        <div className="absolute inset-0 rounded-3xl border-2 border-rose-300/40 animate-ping opacity-20" />
        <div className="absolute inset-1 rounded-3xl border border-rose-200/30 animate-pulse opacity-30" />
      </div>
    );
  };

  const RobotFlowIcon = () => {
    const currentSize = iconSizes[size];
    const gradientColors = getGradientColors();

    return (
      <div className={`${sizeClasses[size]} bg-gradient-to-br ${gradientColors} rounded-3xl flex items-center justify-center shadow-2xl border-2 border-white/40 backdrop-blur-sm relative overflow-hidden group`}>
        <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-200/20 via-cyan-200/10 to-indigo-200/20 opacity-80" />

        <svg
          className={currentSize.width}
          viewBox={currentSize.viewBox}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Robot head */}
          <rect x="12" y="8" width="24" height="20" rx="4" fill="url(#robotHead)" opacity="0.95" />
          <rect x="12" y="8" width="24" height="20" rx="4" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" />

          {/* Robot eyes */}
          <rect x="16" y="14" width="4" height="4" rx="2" fill="url(#eyeRed)" opacity="0.9" />
          <rect x="28" y="14" width="4" height="4" rx="2" fill="url(#eyeRed)" opacity="0.9" />
          <circle cx="18" cy="16" r="1.5" fill="rgba(255,255,255,0.8)" opacity="0.9" />
          <circle cx="30" cy="16" r="1.5" fill="rgba(255,255,255,0.8)" opacity="0.9" />

          {/* Robot antenna */}
          <line x1="24" y1="8" x2="24" y2="4" stroke="rgba(255,255,255,0.6)" strokeWidth="2" />
          <circle cx="24" cy="4" r="2" fill="url(#antenna)" opacity="0.9" />

          {/* Robot body */}
          <rect x="16" y="28" width="16" height="12" rx="2" fill="url(#robotBody)" opacity="0.9" />
          <rect x="16" y="28" width="16" height="12" rx="2" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" />

          {/* Task processing slots */}
          <rect x="18" y="30" width="2" height="8" fill="url(#slot1)" opacity="0.8" />
          <rect x="21" y="30" width="2" height="8" fill="url(#slot2)" opacity="0.8" />
          <rect x="24" y="30" width="2" height="8" fill="url(#slot3)" opacity="0.8" />
          <rect x="27" y="30" width="2" height="8" fill="url(#slot4)" opacity="0.8" />

          {/* Task items in slots */}
          <rect x="18" y="32" width="2" height="1.5" fill="rgba(255,255,255,0.7)" opacity="0.9" />
          <rect x="18" y="34" width="2" height="1.5" fill="rgba(255,255,255,0.7)" opacity="0.9" />
          <rect x="18" y="36" width="2" height="1.5" fill="rgba(255,255,255,0.7)" opacity="0.9" />

          <rect x="21" y="31" width="2" height="1.5" fill="rgba(255,255,255,0.7)" opacity="0.9" />
          <rect x="21" y="33" width="2" height="1.5" fill="rgba(255,255,255,0.7)" opacity="0.9" />
          <rect x="21" y="35" width="2" height="1.5" fill="rgba(255,255,255,0.7)" opacity="0.9" />
          <rect x="21" y="37" width="2" height="1.5" fill="rgba(255,255,255,0.7)" opacity="0.9" />

          <rect x="24" y="32" width="2" height="1.5" fill="rgba(255,255,255,0.7)" opacity="0.9" />
          <rect x="24" y="34" width="2" height="1.5" fill="rgba(255,255,255,0.7)" opacity="0.9" />
          <rect x="24" y="36" width="2" height="1.5" fill="rgba(255,255,255,0.7)" opacity="0.9" />

          <rect x="27" y="31" width="2" height="1.5" fill="rgba(255,255,255,0.7)" opacity="0.9" />
          <rect x="27" y="33" width="2" height="1.5" fill="rgba(255,255,255,0.7)" opacity="0.9" />
          <rect x="27" y="35" width="2" height="1.5" fill="rgba(255,255,255,0.7)" opacity="0.9" />

          {/* Processing indicators */}
          <circle cx="20" cy="42" r="1" fill="rgba(255,255,255,0.8)" opacity="0.9" />
          <circle cx="22" cy="42" r="1" fill="rgba(255,255,255,0.8)" opacity="0.9" />
          <circle cx="24" cy="42" r="1" fill="rgba(255,255,255,0.8)" opacity="0.9" />
          <circle cx="26" cy="42" r="1" fill="rgba(255,255,255,0.8)" opacity="0.9" />
          <circle cx="28" cy="42" r="1" fill="rgba(255,255,255,0.8)" opacity="0.9" />

          {/* Flow arrows */}
          <path d="M10 20 L12 20 M10 24 L12 24 M10 28 L12 28" stroke="rgba(255,255,255,0.6)" strokeWidth="1" opacity="0.7" />
          <path d="M36 20 L38 20 M36 24 L38 24 M36 28 L38 28" stroke="rgba(255,255,255,0.6)" strokeWidth="1" opacity="0.7" />

          {/* Data flow particles */}
          <circle cx="14" cy="20" r="0.6" fill="rgba(255,255,255,0.9)" opacity="1" />
          <circle cx="34" cy="24" r="0.5" fill="rgba(255,255,255,0.9)" opacity="1" />
          <circle cx="14" cy="28" r="0.4" fill="rgba(255,255,255,0.9)" opacity="1" />
          <circle cx="34" cy="20" r="0.6" fill="rgba(255,255,255,0.9)" opacity="1" />
          <circle cx="14" cy="24" r="0.5" fill="rgba(255,255,255,0.9)" opacity="1" />

          <defs>
            <linearGradient id="robotHead" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3B82F6" />
              <stop offset="100%" stopColor="#1D4ED8" />
            </linearGradient>
            <linearGradient id="robotBody" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#06B6D4" />
              <stop offset="100%" stopColor="#0891B2" />
            </linearGradient>
            <radialGradient id="eyeRed" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#EF4444" />
              <stop offset="100%" stopColor="#DC2626" />
            </radialGradient>
            <radialGradient id="antenna" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#FBBF24" />
              <stop offset="100%" stopColor="#F59E0B" />
            </radialGradient>
            <linearGradient id="slot1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#10B981" />
              <stop offset="100%" stopColor="#059669" />
            </linearGradient>
            <linearGradient id="slot2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F59E0B" />
              <stop offset="100%" stopColor="#D97706" />
            </linearGradient>
            <linearGradient id="slot3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#8B5CF6" />
              <stop offset="100%" stopColor="#7C3AED" />
            </linearGradient>
            <linearGradient id="slot4" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#EC4899" />
              <stop offset="100%" stopColor="#DB2777" />
            </linearGradient>
          </defs>
        </svg>

        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-2000 ease-in-out" />
        <div className="absolute inset-0 rounded-3xl border-2 border-blue-300/40 animate-ping opacity-20" />
        <div className="absolute inset-1 rounded-3xl border border-blue-200/30 animate-pulse opacity-30" />

        {/* Robot processing lights */}
        <div className="absolute top-2 left-2 w-2 h-2 bg-green-400 rounded-full animate-pulse opacity-60" />
        <div className="absolute top-2 right-2 w-2 h-2 bg-yellow-400 rounded-full animate-pulse opacity-60" style={{animationDelay: '0.5s'}} />
      </div>
    );
  };

  const getLogoIcon = () => {
    switch (variant) {
      case 'hourglass':
        return HourglassFlowIcon;
      case 'galaxy':
        return GalaxyFlowIcon;
      case 'dna':
        return DNAFlowIcon;
      case 'robot':
        return RobotFlowIcon;
      case 'river':
        return () => {
          const currentSize = iconSizes[size];
          const gradientColors = getGradientColors();

          return (
            <div className={`${sizeClasses[size]} bg-gradient-to-br ${gradientColors} rounded-3xl flex items-center justify-center shadow-2xl border-2 border-white/40 backdrop-blur-sm relative overflow-hidden group`}>
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="absolute inset-0 bg-gradient-to-br from-blue-200/20 via-cyan-200/10 to-teal-200/20 opacity-80" />

              <svg className={currentSize.width} viewBox={currentSize.viewBox} fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 32 Q24 28 44 32 Q48 34 48 38 Q44 42 24 38 Q4 42 0 38 Q0 34 4 32" fill="url(#riverGrad)" opacity="0.3" />
                <path d="M2 24 Q12 20 22 22 Q32 24 42 22 Q48 24 48 28 Q42 32 32 30 Q22 28 12 30 Q2 32 2 28 Z" fill="url(#waterGrad)" opacity="0.9" />
                <ellipse cx="12" cy="24" rx="8" ry="2" fill="white" opacity="0.4" />
                <ellipse cx="24" cy="26" rx="6" ry="1.5" fill="white" opacity="0.3" />
                <ellipse cx="36" cy="24" rx="8" ry="2" fill="white" opacity="0.4" />
                <ellipse cx="10" cy="24" rx="3" ry="1.5" fill="url(#stoneGrad1)" opacity="0.9" />
                <ellipse cx="18" cy="26" rx="2.5" ry="1.2" fill="url(#stoneGrad2)" opacity="0.9" />
                <ellipse cx="26" cy="24" rx="3" ry="1.5" fill="url(#stoneGrad3)" opacity="0.9" />
                <ellipse cx="34" cy="26" rx="2.5" ry="1.2" fill="url(#stoneGrad4)" opacity="0.9" />
                <ellipse cx="42" cy="24" rx="3" ry="1.5" fill="url(#stoneGrad5)" opacity="0.9" />

                <defs>
                  <linearGradient id="riverGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#10B981" />
                    <stop offset="100%" stopColor="#047857" />
                  </linearGradient>
                  <linearGradient id="waterGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#06B6D4" />
                    <stop offset="100%" stopColor="#0E7490" />
                  </linearGradient>
                  <radialGradient id="stoneGrad1" cx="50%" cy="40%" r="60%">
                    <stop offset="0%" stopColor="#6B7280" />
                    <stop offset="100%" stopColor="#374151" />
                  </radialGradient>
                  <radialGradient id="stoneGrad2" cx="50%" cy="40%" r="60%">
                    <stop offset="0%" stopColor="#9CA3AF" />
                    <stop offset="100%" stopColor="#6B7280" />
                  </radialGradient>
                  <radialGradient id="stoneGrad3" cx="50%" cy="40%" r="60%">
                    <stop offset="0%" stopColor="#4B5563" />
                    <stop offset="100%" stopColor="#1F2937" />
                  </radialGradient>
                  <radialGradient id="stoneGrad4" cx="50%" cy="40%" r="60%">
                    <stop offset="0%" stopColor="#D1D5DB" />
                    <stop offset="100%" stopColor="#9CA3AF" />
                  </radialGradient>
                  <radialGradient id="stoneGrad5" cx="50%" cy="40%" r="60%">
                    <stop offset="0%" stopColor="#374151" />
                    <stop offset="100%" stopColor="#111827" />
                  </radialGradient>
                </defs>
              </svg>

              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-2000 ease-in-out" />
              <div className="absolute inset-0 rounded-3xl border-2 border-cyan-300/40 animate-ping opacity-20" />
              <div className="absolute inset-1 rounded-3xl border border-cyan-200/30 animate-pulse opacity-30" />
            </div>
          );
        };
      case 'brain':
        return () => {
          const currentSize = iconSizes[size];
          const gradientColors = getGradientColors();

          return (
            <div className={`${sizeClasses[size]} bg-gradient-to-br ${gradientColors} rounded-3xl flex items-center justify-center shadow-2xl border-2 border-white/40 backdrop-blur-sm relative overflow-hidden group`}>
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="absolute inset-0 bg-gradient-to-br from-purple-200/20 via-pink-200/10 to-indigo-200/20 opacity-80" />

              <svg className={currentSize.width} viewBox={currentSize.viewBox} fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 24 Q4 20 6 16 Q8 12 12 10 Q16 8 20 10 Q24 12 28 10 Q32 8 36 10 Q40 12 42 16 Q44 20 40 24 Q36 28 32 26 Q28 24 24 26 Q20 28 16 26 Q12 24 8 24" fill="url(#brainGrad)" opacity="0.9" />
                <path d="M12 18 Q20 14 28 18 Q32 20 34 24 Q32 28 28 26 Q20 22 12 26" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1" opacity="0.8" />
                <circle cx="14" cy="18" r="1.5" fill="rgba(255,255,255,0.8)" opacity="0.9" />
                <circle cx="20" cy="16" r="1.2" fill="rgba(255,255,255,0.8)" opacity="0.9" />
                <circle cx="24" cy="20" r="1.5" fill="rgba(255,255,255,0.8)" opacity="0.9" />
                <circle cx="28" cy="18" r="1.2" fill="rgba(255,255,255,0.8)" opacity="0.9" />

                <defs>
                  <radialGradient id="brainGrad" cx="50%" cy="40%" r="70%">
                    <stop offset="0%" stopColor="#A855F7" />
                    <stop offset="100%" stopColor="#7C3AED" />
                  </radialGradient>
                </defs>
              </svg>

              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-2000 ease-in-out" />
              <div className="absolute inset-0 rounded-3xl border-2 border-purple-300/40 animate-ping opacity-20" />
              <div className="absolute inset-1 rounded-3xl border border-purple-200/30 animate-pulse opacity-30" />
            </div>
          );
        };
      case 'rocket':
        return () => {
          const currentSize = iconSizes[size];
          const gradientColors = getGradientColors();

          return (
            <div className={`${sizeClasses[size]} bg-gradient-to-br ${gradientColors} rounded-3xl flex items-center justify-center shadow-2xl border-2 border-white/40 backdrop-blur-sm relative overflow-hidden group`}>
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="absolute inset-0 bg-gradient-to-br from-orange-200/20 via-red-200/10 to-pink-200/20 opacity-80" />

              <svg className={currentSize.width} viewBox={currentSize.viewBox} fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M24 40 L26 44 L22 44 Z" fill="url(#trailGrad)" opacity="0.7" />
                <ellipse cx="24" cy="24" rx="8" ry="12" fill="url(#rocketGrad)" opacity="0.95" />
                <path d="M24 8 L20 12 L24 14 L28 12 Z" fill="url(#noseGrad)" opacity="0.9" />
                <circle cx="24" cy="20" r="3" fill="url(#windowGrad)" opacity="0.9" />
                <circle cx="24" cy="20" r="1.5" fill="rgba(255,255,255,0.8)" opacity="0.9" />

                <defs>
                  <linearGradient id="trailGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#F59E0B" />
                    <stop offset="100%" stopColor="#EC4899" />
                  </linearGradient>
                  <linearGradient id="rocketGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#F59E0B" />
                    <stop offset="100%" stopColor="#EA580C" />
                  </linearGradient>
                  <linearGradient id="noseGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FDE68A" />
                    <stop offset="100%" stopColor="#F59E0B" />
                  </linearGradient>
                  <radialGradient id="windowGrad" cx="50%" cy="40%" r="60%">
                    <stop offset="0%" stopColor="#3B82F6" />
                    <stop offset="100%" stopColor="#1D4ED8" />
                  </radialGradient>
                </defs>
              </svg>

              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-2000 ease-in-out" />
              <div className="absolute inset-0 rounded-3xl border-2 border-orange-300/40 animate-ping opacity-20" />
              <div className="absolute inset-1 rounded-3xl border border-orange-200/30 animate-pulse opacity-30" />
            </div>
          );
        };
      case 'compass':
        return () => {
          const currentSize = iconSizes[size];
          const gradientColors = getGradientColors();

          return (
            <div className={`${sizeClasses[size]} bg-gradient-to-br ${gradientColors} rounded-3xl flex items-center justify-center shadow-2xl border-2 border-white/40 backdrop-blur-sm relative overflow-hidden group`}>
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-200/20 via-teal-200/10 to-cyan-200/20 opacity-80" />

              <svg className={currentSize.width} viewBox={currentSize.viewBox} fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="18" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
                <circle cx="24" cy="24" r="14" fill="url(#compassGrad)" opacity="0.9" />
                <path d="M24 8 L22 14 L26 14 Z" fill="rgba(255,255,255,0.9)" opacity="0.9" />
                <g transform="translate(24,24)">
                  <path d="M0 -8 L-2 -2 L2 -2 Z" fill="url(#needleGrad)" opacity="0.95" />
                  <path d="M0 8 L-2 2 L2 2 Z" fill="url(#needleGrad2)" opacity="0.95" />
                </g>

                <defs>
                  <radialGradient id="compassGrad" cx="50%" cy="40%" r="70%">
                    <stop offset="0%" stopColor="#10B981" />
                    <stop offset="100%" stopColor="#047857" />
                  </radialGradient>
                  <linearGradient id="needleGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#EF4444" />
                    <stop offset="100%" stopColor="#DC2626" />
                  </linearGradient>
                  <linearGradient id="needleGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#F59E0B" />
                    <stop offset="100%" stopColor="#D97706" />
                  </linearGradient>
                </defs>
              </svg>

              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-2000 ease-in-out" />
              <div className="absolute inset-0 rounded-3xl border-2 border-emerald-300/40 animate-ping opacity-20" />
              <div className="absolute inset-1 rounded-3xl border border-emerald-200/30 animate-pulse opacity-30" />
            </div>
          );
        };
      case 'eye':
        return () => {
          const currentSize = iconSizes[size];
          const gradientColors = getGradientColors();

          return (
            <div className={`${sizeClasses[size]} bg-gradient-to-br ${gradientColors} rounded-3xl flex items-center justify-center shadow-2xl border-2 border-white/40 backdrop-blur-sm relative overflow-hidden group`}>
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-200/20 via-purple-200/10 to-pink-200/20 opacity-80" />

              <svg className={currentSize.width} viewBox={currentSize.viewBox} fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="24" cy="24" rx="18" ry="15" fill="white" opacity="0.98" stroke="rgba(255,255,255,0.6)" strokeWidth="0.5" />
                <circle cx="24" cy="24" r="12" fill="url(#irisGrad)" opacity="0.95" />
                <circle cx="24" cy="24" r="7" fill="url(#pupilGrad)" />
                <ellipse cx="20" cy="20" rx="3" ry="4" fill="white" opacity="0.9" />
                <ellipse cx="28" cy="18" rx="1.5" ry="2" fill="white" opacity="0.7" />
                <circle cx="22" cy="22" r="1.2" fill="rgba(255,255,255,0.7)" opacity="0.9" />
                <circle cx="26" cy="23" r="1" fill="rgba(255,255,255,0.7)" opacity="0.9" />
                <circle cx="23" cy="26" r="0.8" fill="rgba(255,255,255,0.7)" opacity="0.9" />

                <defs>
                  <radialGradient id="irisGrad" cx="45%" cy="35%" r="70%">
                    <stop offset="0%" stopColor="#A855F7" />
                    <stop offset="50%" stopColor="#8B5CF6" />
                    <stop offset="100%" stopColor="#7C3AED" />
                  </radialGradient>
                  <radialGradient id="pupilGrad" cx="45%" cy="35%" r="70%">
                    <stop offset="0%" stopColor="#2D1B69" />
                    <stop offset="100%" stopColor="#000000" />
                  </radialGradient>
                </defs>
              </svg>

              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-2000 ease-in-out" />
              <div className="absolute inset-0 rounded-3xl border-2 border-purple-300/40 animate-ping opacity-20" />
              <div className="absolute inset-1 rounded-3xl border border-purple-200/30 animate-pulse opacity-30" />
            </div>
          );
        };
      default:
        return HourglassFlowIcon;
    }
  };

  const LogoIcon = getLogoIcon();

  const TextElement = () => {
    const textGradient = getTextGradient();

    return (
      <motion.span
        className={`font-bold ${textSizeClasses[size]} bg-gradient-to-r ${textGradient} bg-clip-text text-transparent tracking-wide`}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        TaskFlow
      </motion.span>
    );
  };

  if (animated) {
    return (
      <motion.div
        className="flex items-center space-x-4 cursor-pointer"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <LogoIcon />
        {showText && <TextElement />}
      </motion.div>
    );
  }

  return (
    <div className="flex items-center space-x-4">
      <LogoIcon />
      {showText && (
        <span className={`font-bold ${textSizeClasses[size]} bg-gradient-to-r ${getTextGradient()} bg-clip-text text-transparent tracking-wide`}>
          TaskFlow
        </span>
      )}
    </div>
  );
};

export default TaskFlowLogo;
